import React, { useState } from "react";
import { Form, Row, Col, Button, InputGroup } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Icon } from '@iconify/react';
import logo from "../assets/img/logo-dark.svg";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { login } from "../api/authService";
import { useAuthStore } from "../store/useAuthStore";
import { loginSchema } from "../validations/authSchema";

export default function Login() {
  const { login: loginUser } = useAuthStore();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [apiError, setApiError] = useState('');
  
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setError,
    clearErrors
  } = useForm({
    resolver: yupResolver(loginSchema),
  });
  
  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const onLoginSubmit = async (formData) => {
    if (isSubmitting) return;
    
    // Clear previous errors
    setApiError('');
    clearErrors();
    toast.dismiss();
    
    // Trim email and password
    const data = {
      email: formData.email.trim(),
      password: formData.password
    };
    
    setIsSubmitting(true);
    
    try {
      const res = await login(data.email, data.password);

      if (res.status === true && res.code === 200) {
        loginUser(res.data, res.data.token);
        toast.success(res.message || 'Login successful!', { toastId: 'login-success' });
        navigate("/dashboard");
        reset();
      } else {
        // Handle different error cases
        if (res.message?.toLowerCase().includes('email') || res.code === 404) {
          setError('email', { type: 'manual', message: 'No account found with this email' });
        } else if (res.message?.toLowerCase().includes('password') || res.code === 401) {
          setError('password', { type: 'manual', message: 'Incorrect password' });
        } else {
          toast.error(res.message || 'Login failed. Please try again.');
        }
      }
    } catch (err) {
    
      toast.error(err.message || 'An error occurred during login. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Add custom styles to hide the invalid feedback icon
  const formControlStyle = {
    height: '48px',
    borderRadius: '6px',
    border: '1px solid #ced4da',
    transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
    backgroundImage: 'none !important', // Hide the background image (invalid icon)
    paddingRight: '12px' // Ensure consistent padding
  };

  return (
    <React.Fragment>
      <Row className="justify-content-center">
        <Col lg={6} className="loginform">
          <div className="loginmain_div maxwidth500 mx-auto">
            <div className="headlogin_div mb-4">
              <img className="loginlogo_img" src={logo} alt="" />
              <h2>Welcome To OneLifeTale</h2>
            </div>
            <Form onSubmit={handleSubmit(onLoginSubmit)}>
              <Form.Group className="mb-4 form-group">
                <Form.Label>Email Address</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  placeholder="Enter email address"
                  {...register("email")}
                  isInvalid={false}
                  // className={errors.email ? 'is-invalid' : ''}
                  style={{
                    ...formControlStyle,
                    borderColor: errors.email ? '#dc3545' : '#ced4da',
                    paddingRight: '12px' // Explicitly set right padding for email
                  }}
                  onBlur={(e) => (e.target.value = e.target.value.replace(/\s+$/, ""))}
                />
                {errors.email && (
                  <div className="invalid-feedback d-block" style={{ marginTop: '0.25rem' }}>
                    {errors.email.message}
                  </div>
                )}
              </Form.Group>
              <Form.Group className="mb-4 form-group" style={{ position: 'relative' }}>
                <Form.Label>Password</Form.Label>
                <div style={{ position: 'relative' }}>
                  <Form.Control
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="Enter password"
                    {...register("password")}
                    isInvalid={false}
                    // className={errors.password ? 'is-invalid' : ''}
                    autoComplete="current-password"
                    style={{
                      ...formControlStyle,
                      paddingRight: '40px', // Keep extra padding for the eye icon
                      borderColor: errors.password ? '#dc3545' : '#ced4da'
                    }}
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    style={{
                      position: 'absolute',
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: errors.password ? '#dc3545' : '#6c757d',
                      outline: 'none',
                      zIndex: 5
                    }}
                    onMouseOver={(e) => (e.currentTarget.style.color = errors.password ? '#bb2d3b' : '#495057')}
                    onMouseOut={(e) => (e.currentTarget.style.color = errors.password ? '#dc3545' : '#6c757d')}
                  >
                    <Icon 
                      icon={showPassword ? 'mdi:eye-off' : 'mdi:eye'} 
                      style={{ fontSize: '1.25rem' }}
                    />
                  </button>
                </div>
                {errors.password && (
                  <div className="invalid-feedback d-block" style={{ marginTop: '0.25rem' }}>
                    {errors.password.message}
                  </div>
                )}
              </Form.Group>
              
              {apiError && (
                <div className="alert alert-danger" role="alert">
                  {apiError}
                </div>
              )}
              <Form.Group className="mb-4 form-group forgotpassword text-end">
                <Link to="/forgot-password">Forgot Password?</Link>
              </Form.Group>

              <Button
                type="submit"
                value="Login"
                className="btn btn-primary w-100"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Logging in...
                  </>
                ) : (
                  'Login'
                )}
              </Button>
            </Form>
          </div>
        </Col>
      </Row>
    </React.Fragment>
  );
}
